import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

type Variant = 'primary' | 'gold' | 'outline' | 'ghost' | 'danger';
type Size    = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary: 'bg-navy-dark text-white hover:bg-navy-darker shadow',
  gold:    'bg-gold text-white hover:bg-gold-dark shadow',
  outline: 'border-2 border-navy-dark text-navy-dark hover:bg-navy hover:text-white',
  ghost:   'bg-transparent text-navy-dark hover:bg-gray-100',
  danger:  'bg-red-600 text-white hover:bg-red-700 shadow',
};

const sizes: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-7 py-3 text-base',
};

export function Button({
  variant = 'primary', size = 'md',
  leftIcon, rightIcon, loading, disabled, className, children, ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl font-bold transition disabled:opacity-50 disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {loading ? <span className="animate-spin">⟳</span> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}
